import * as XLSX from 'xlsx';

export interface CompanyData {
  id?: number;
  company_name: string;
  state?: string;
  entity_type?: string;
  category?: string;
  nickname?: string;
  federal_tax_id?: string;
  state_tax_id?: string;
  sos_id?: string;
  boir_id?: string;
  duns?: string;
  registration_date?: string;
  status?: string;
  sos_registrant?: string;
  next_file?: string;
  sos_filing_date?: string;
  owner_member?: string;
  ceo_manager?: string;
  cfo_tax_rep?: string;
  secretary?: string;
  director?: string;
  principal_address?: string;
  mailing_address?: string;
  state_office_address?: string;
  registered_agent_name?: string;
  registered_agent_address?: string;
  business_description?: string;
  naics_code?: string;
  naics_description?: string;
  sic_code?: string;
  sic_description?: string;
  sos_renewal_comment?: string;
  by_law?: string;
  stock_documents?: string;
  company_kit?: string;
  termination_date?: string;
  check_mark?: string;
  operating_agreement?: string;
  follow_up?: string;
  payment?: string;
  ein_letter?: string;
  irs_registrant?: string;
  employee_count?: string;
  website?: string;
  username?: string;
  password?: string;
  question?: string;
  six_month?: string;
  five_month?: string;
  cn_4p?: string;
  us_manager?: string;
  group_name?: string;
  has_us_staff?: string;
  tier?: string;
  notes?: string;
}

export interface ExpenseData {
  id?: number;
  application_date?: string;
  expense_company: string;
  expense_purpose?: string;
  warehouse?: string;
  unit_price?: number;
  quantity?: number;
  unit?: string;
  subtotal?: number;
  payment_method?: string;
  expense_cycle?: string;
  approval_text?: string;
  approval_role?: string;
  approver?: string;
  status?: string;
  remark?: string;
  creator?: string;
}

export interface LicenseData {
  id?: number;
  company_id?: number;
  company_name: string;
  location?: string;
  state?: string;
  level?: string;
  department?: string;
  category?: string;
  license_type?: string;
  certificate_name?: string;
  license_no?: string;
  registration_date?: string;
  renewal_type?: string;
  expiration_date: string;
  renewal_date?: string;
  status?: string;
  renewal_fee?: number;
  conv_fee?: number;
  fee_comment?: string;
  payment_method?: string;
  renewal_method?: string;
  renewal_guide?: string;
  renewal_info?: string;
  info_period?: string;
  info_provider?: string;
  notice_invoice?: string;
  website?: string;
  account?: string;
  password_pin?: string;
  questions?: string;
  contact_person?: string;
  phone_number?: string;
  contact_email?: string;
  follow_up?: string;
  year_2017?: string;
  year_2018?: string;
  year_2019?: string;
  year_2020?: string;
  year_2021?: string;
  year_2022?: string;
  year_2023?: string;
  year_2024?: string;
  year_2025?: string;
  year_2026?: string;
  remark?: string;
  external_coin?: string;
  internal_coin?: string;
  qtr?: string;
}

export const parseExcelFile = async (file: File): Promise<{ sheets: string[]; data: Record<string, any[]> }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheets: string[] = workbook.SheetNames;
        const result: Record<string, any[]> = {};
        
        sheets.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          // 使用默认设置，自动将第一行作为表头
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (!Array.isArray(jsonData) || jsonData.length === 0) {
            result[sheetName] = [];
            return;
          }
          
          result[sheetName] = jsonData;
        });
        
        resolve({ sheets, data: result });
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};

export const convertToCompanyData = (rawData: any[]): CompanyData[] => {
  return rawData.map((row) => ({
    company_name: row['Company Name'] || row['公司名称'] || '',
    state: row['State'] || '',
    entity_type: row['Entity Type'] || '',
    category: row['Category'] || '',
    nickname: row['Nickname'] || '',
    federal_tax_id: row['Federal Tax ID'] || '',
    state_tax_id: row['State Tax ID'] || row['State ID'] || '',
    sos_id: row['SOS ID'] || '',
    boir_id: row['BOIR ID（BOI）'] || row['BOIR ID'] || row['BOI'] || '',
    duns: row['DUNS#'] || row['DUNS'] || '',
    registration_date: formatDate(row['Registration Date'] || ''),
    status: row['Status'] || '',
    sos_registrant: row['SOS Registrant'] || '',
    next_file: row['Next File'] || '',
    sos_filing_date: formatDate(row['SOS Filing Date'] || ''),
    owner_member: row['Owner/Member'] || '',
    ceo_manager: row['CEO/Manager'] || '',
    cfo_tax_rep: row['CFO/Tax Rep.'] || '',
    secretary: row['Secretary'] || '',
    director: row['Director（4P/5M）'] || row['Director'] || '',
    principal_address: row['Principal Address'] || '',
    mailing_address: row['Mailing Address'] || '',
    state_office_address: row['State Office Address'] || '',
    registered_agent_name: row['Registered Agent Name'] || '',
    registered_agent_address: row['Registered Agent Address'] || '',
    business_description: row['Business Description'] || '',
    naics_code: row['NAICS Code'] || '',
    naics_description: row['NAICS Description'] || '',
    sic_code: row['SIC Code'] || '',
    sic_description: row['SIC Description'] || '',
    sos_renewal_comment: row['SOS Renewal Commnet'] || row['SOS Renewal Comment'] || '',
    by_law: row['By-Law (INC)'] || '',
    stock_documents: row['Stock Documents'] || '',
    company_kit: row['Company Kit'] || '',
    termination_date: formatDate(row['Termination Date'] || ''),
    check_mark: row['✔'] || row['Check Mark'] || '',
    operating_agreement: row['Operating Agreement'] || '',
    follow_up: row['待跟進'] || row['Follow Up'] || '',
    payment: row['Payment'] || '',
    ein_letter: row['EIN Letter'] || '',
    irs_registrant: row['IRS Registrant'] || '',
    employee_count: row['員工人數'] || row['Employee Count'] || '',
    website: row['Website'] || '',
    username: row['Username'] || '',
    password: row['Password'] || '',
    question: row['Question'] || '',
    six_month: row['6M'] || '',
    five_month: row['5M'] || '',
    cn_4p: row['CN 4P'] || '',
    us_manager: row['US負責人'] || row['US Manager'] || '',
    group_name: (row['GROUP(這個哦！）'] || row['GROUP'] || row['Group'] || '').toString().trim(),
    has_us_staff: row['是否有US現場人員'] || row['Has US Staff'] || '',
    tier: row['Tier'] || '',
    notes: row['備註'] || row['Remark'] || '',
  })).filter(c => c.company_name);
};

export const convertToLicenseData = (rawData: any[]): LicenseData[] => {
  console.log('Raw license data:', rawData);
  const converted = rawData.map((row) => ({
    // 基本信息
    // License uses the short nickname (B column: "Company") for company_name
    company_name:
      row['Company'] ??
      row['Company '] ??
      row['company'] ??
      row['Company Name'] ??
      row['Company Name '] ??
      row['公司名称'] ??
      row['公司名稱'] ??
      '',
    location: row['Location'] || row['location'] || row['Location '] || '',
    state: row['State'] || row['state'] || row['State '] || '',
    level: row['Level'] || row['level'] || row['Level '] || '',
    department: row['Department'] || row['department'] || row['Department '] || '',
    category: row['Category'] || row['category'] || row['Category '] || '',
    license_type: row['License Type'] || row['license_type'] || row['licenseType'] || row['License Type '] || '',
    certificate_name: row['Certificate Name'] || row['certificate_name'] || row['License Name'] || row['license_name'] || row['Certificate Name '] || '',
    license_no: row['License No'] || row['License No.'] || row['license_no'] || row['License Number'] || row['license_number'] || row['License No '] || '',
    
    // 日期字段
    registration_date: formatDate(row['Registration Date'] || row['registration_date'] || row['register_date'] || row['Registration Date '] || ''),
    renewal_type: row['Renewal Type'] || row['renewal_type'] || row['Renewal Type '] || '',
    expiration_date: formatDate(row['Expiration Date'] || row['expiration_date'] || row['expire_date'] || row['Expire Date'] || row['Expiration Date '] || ''),
    renewal_date: formatDate(row['Renewal Date'] || row['renewal_date'] || row['Renewal Date '] || ''),
    status: row['Status'] || row['status'] || row['Status '] || '',
    
    // 费用相关
    renewal_fee: parseFloat(String(row['Renewal Fee'] || row['renewal_fee'] || row['Renewal Fee '] || '0').replace(/[\$,]/g, '')),
    conv_fee: parseFloat(String(row['Conv. Fee'] || row['conv_fee'] || row['Conv. Fee '] || '0').replace(/[\$,]/g, '')),
    fee_comment: row['Fee Comment'] || row['fee_comment'] || row['Fee Comment '] || '',
    
    // 续期相关
    payment_method: row['Payment Method'] || row['payment_method'] || row['Payment Method '] || '',
    renewal_method: row['Renewal Method'] || row['renewal_method'] || row['Renewal Method '] || '',
    renewal_guide: row['Renewal指引'] || row['Renewal Guide'] || row['renewal_guide'] || row['Renewal Guide '] || '',
    renewal_info: row['Renewal Info'] || row['renewal_info'] || row['Renewal Info '] || '',
    info_period: row['Info Period'] || row['info_period'] || row['Info Period '] || '',
    info_provider: row['Info Provider'] || row['info_provider'] || row['Info Provider '] || '',
    notice_invoice: row['Notice/Invoice'] || row['notice_invoice'] || row['Notice/Invoice '] || '',
    
    // 网站账户
    website: row['Website'] || row['website'] || row['Website '] || '',
    account: row['Account'] || row['account'] || row['Account '] || '',
    password_pin: row['Password/Pin'] || row['password_pin'] || row['Password/Pin '] || '',
    questions: row['Questions'] || row['questions'] || row['Questions '] || '',
    
    // 联系人
    contact_person: row['Contact Person'] || row['contact_person'] || row['Contact Person '] || '',
    phone_number: row['Phone Number'] || row['phone_number'] || row['Phone Number '] || '',
    contact_email: row['Contact Email'] || row['contact_email'] || row['Contact Email '] || '',
    follow_up: row['Follow Up'] || row['follow_up'] || row['Follow Up '] || '',
    
    // 历史费用（多个年份）
    year_2017: row['2017'] || row['年2017'] || row['歷史費用2017'] || '',
    year_2018: row['2018'] || row['年2018'] || row['歷史費用2018'] || '',
    year_2019: row['2019'] || row['年2019'] || row['歷史費用2019'] || '',
    year_2020: row['2020'] || row['年2020'] || row['歷史費用2020'] || '',
    year_2021: row['2021'] || row['年2021'] || row['歷史費用2021'] || '',
    year_2022: row['2022'] || row['年2022'] || row['歷史費用2022'] || '',
    year_2023: row['2023'] || row['年2023'] || row['歷史費用2023'] || '',
    year_2024: row['2024'] || row['年2024'] || row['歷史費用2024'] || '',
    year_2025: row['2025'] || row['年2025'] || row['歷史費用2025'] || '',
    year_2026: row['2026'] || row['年2026'] || row['歷史費用2026'] || '',
    
    // 其他
    remark: row['備註'] || row['Remark'] || row['remark'] || row['Remark '] || row['備註 '] || '',
    external_coin: row['對外COIN'] || row['External COIN'] || row['external_coin'] || row['對外COIN '] || '',
    internal_coin: row['對內COIN'] || row['Internal COIN'] || row['internal_coin'] || row['對內COIN '] || '',
    qtr: row['Qtr'] || row['qtr'] || row['Qtr '] || '',
  }));
  
  console.log('Converted license data:', converted);
  // 放宽过滤条件 - 只要有company_name或certificate_name就接受
  return converted.filter(l => l.company_name || l.certificate_name);
};

export const convertToExpenseData = (rawData: any[]): ExpenseData[] => {
  const converted = rawData.map((row) => {
    const priceStr = (row['Unit Price'] || row['unit_price'] || row['單價'] || '0').toString().replace(/[\$,\s]/g, '');
    const unitPrice = parseFloat(priceStr) || 0;
    
    const qtyStr = (row['Quantity'] || row['quantity'] || row['數量'] || '0').toString().replace(/\s/g, '');
    const quantity = parseInt(qtyStr) || 0;
    
    const expenseCompany = row['Expense Company'] || row['expense_company'] || row['費用公司'] || '';
    
    return {
      application_date: formatDate(row['Application Date'] || row['application_date'] || row['申請日期'] || new Date().toISOString().split('T')[0]),
      expense_company: expenseCompany,
      expense_purpose: row['Expense Purpose'] || row['expense_purpose'] || row['費用用途'] || '',
      warehouse: row['Warehouse'] || row['warehouse'] || row['貨倉'] || '',
      unit_price: unitPrice,
      quantity: quantity,
      unit: row['Unit'] || row['unit'] || row['單位'] || '',
      subtotal: unitPrice * quantity,
      payment_method: row['Payment Method'] || row['payment_method'] || row['支付方式'] || '',
      expense_cycle: row['Expense Cycle'] || row['expense_cycle'] || row['費用週期'] || '',
      remark: row['Remark'] || row['remark'] || row['備註'] || '',
      creator: row['Creator'] || row['creator'] || 'SHELLEY.H',
      approval_role: row['Approval Role'] || row['approval_role'] || row['審批角色'] || '',
      approver: row['Approver'] || row['approver'] || row['審批人'] || '',
      status: row['Status'] || row['status'] || row['狀態'] || '',
      approval_text: row['Approval Text'] || row['approval_text'] || row['審批文字'] || '',
    };
  });
  
  return converted.filter(e => e.expense_company);
};

const formatDate = (dateValue: any): string => {
  if (!dateValue) return '';
  
  if (typeof dateValue === 'string') {
    const dateStr = dateValue.trim();
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
    } else if (dateStr.includes('-')) {
      return dateStr;
    }
  } else if (typeof dateValue === 'number') {
    const date = new Date((dateValue - 25569) * 86400 * 1000);
    return date.toISOString().split('T')[0];
  }
  
  return '';
};
